import type { BaseContactDetail } from '@/api';
import { ContactDetailEdit } from './ContactDetailEdit';
import { ContactDetailView } from './ContactDetailView';

// Define the mode type
export type ContactDetailMode = 'edit' | 'view';
// Base props shared across all modes - used to avoid duplication in discriminated union
export interface ContactDetailBaseProps {
	title?: string;
	subtitle: string;
}

// Mode-specific props using discriminated unions for strict TypeScript
export type ContactDetailProps =
	| (ContactDetailBaseProps & {
			mode: 'edit';
			initialDetails: BaseContactDetail;
			onChange?: (details: BaseContactDetail) => void;
			// contactDetails is not allowed in edit mode - enforce at type level
			contactDetails?: never;
	  })
	| (ContactDetailBaseProps & {
			mode: 'view';
			contactDetails: BaseContactDetail; // Required for view mode
			// Edit mode props are not allowed in view mode - enforce at type level
			initialDetails?: never;
			onChange?: never;
	  });

/**
 * Type guard to check if props are for edit mode
 */
function isEditModeProps(
	props: ContactDetailProps,
): props is ContactDetailProps & { mode: 'edit' } {
	return props.mode === 'edit';
}

/**
 * Type guard to check if props are for view mode
 */
function isViewModeProps(
	props: ContactDetailProps,
): props is ContactDetailProps & { mode: 'view' } {
	return props.mode === 'view';
}

export function ContactDetail(props: ContactDetailProps) {
	if (isEditModeProps(props)) {
		const { title, subtitle, initialDetails, onChange } = props;
		return (
			<ContactDetailEdit
				title={title}
				subtitle={subtitle}
				initialDetails={initialDetails}
				onChange={onChange}
			/>
		);
	}
	if (isViewModeProps(props)) {
		const { title, subtitle, contactDetails } = props;
		return (
			<ContactDetailView
				title={title}
				subtitle={subtitle}
				contactDetails={contactDetails}
			/>
		);
	}
	return null;
}
